import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { Pill, when } from './PlatformConsole';
import { ConfirmAction } from './PlatformPharmacies';

export const PlatformBranchRequests = () => {
  const [state, setState] = useState('pending');
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);     // {branch, decision}
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get('/platform/branch-requests', { params: { state } })
      .then(r => setData(r.data)).catch(() => toast.error('Could not load branch requests'));
  }, [state]);
  useEffect(() => { load(); }, [load]);

  const decide = async (reason) => {
    setBusy(true);
    try {
      await api.post(`/platform/branches/${dialog.branch.branch_id}/decision`,
        { decision: dialog.decision, reason });
      toast.success(`Branch ${dialog.decision.replace('_', ' ')}`);
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Decision failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pc-branch-requests">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Branch approvals</h1>
          <p className="pc-sub">A branch only reaches the public once you approve it.</p>
        </div>
        <select className="pc-select" style={{ width: 190 }} value={state}
          onChange={(e) => setState(e.target.value)} data-testid="pc-branch-state-filter">
          {['pending', 'info_requested', 'approved', 'rejected', 'all'].map(s => (
            <option key={s} value={s}>{s.replace('_', ' ')}{data?.counts?.[s] != null ? ` · ${data.counts[s]}` : ''}</option>
          ))}
        </select>
      </div>

      {!data ? <div className="pc-muted" style={{ marginTop: '1.5rem' }}><Loader2 className="h-4 w-4 animate-spin" /></div> : (
        <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
          <table className="pc-table">
            <thead><tr>
              <th>Branch</th><th>Pharmacy</th><th>Town</th><th>Licence</th><th>Submitted</th><th>State</th><th />
            </tr></thead>
            <tbody>
              {data.requests.map(r => (
                <tr key={r.branch_id} data-testid={`pc-branch-request-${r.branch_id}`}>
                  <td>
                    <b style={{ color: '#fff' }}>{r.name}</b>
                    <div className="pc-muted">{r.address || '—'} · {r.phone || 'no phone'}</div>
                    {r.note && <div className="pc-muted">“{r.note}”</div>}
                    {r.approval_reason && <div className="pc-muted" data-testid={`pc-branch-reason-${r.branch_id}`}>Last reason: “{r.approval_reason}”</div>}
                  </td>
                  <td>
                    <Link to={`/platform/pharmacies/${r.pharmacy_id}`} className="pc-link">{r.pharmacy_name}</Link>
                    <div className="pc-muted">{r.plan} · <Pill value={r.pharmacy_kyc} /></div>
                  </td>
                  <td>{r.town || '—'}</td>
                  <td className="pc-mono">{r.licence_reference || '—'}</td>
                  <td>{when(r.submitted_at)}</td>
                  <td><Pill value={r.approval_status} /></td>
                  <td className="pc-num" style={{ whiteSpace: 'nowrap' }}>
                    {r.approval_status !== 'approved' && (
                      <button className="pc-btn pc-btn-primary" onClick={() => setDialog({ branch: r, decision: 'approved' })}
                        data-testid={`pc-branch-approve-${r.branch_id}`}>Approve</button>
                    )}{' '}
                    <button className="pc-btn" onClick={() => setDialog({ branch: r, decision: 'info_requested' })}
                      data-testid={`pc-branch-info-${r.branch_id}`}>Ask for info</button>{' '}
                    <button className="pc-btn pc-btn-danger" onClick={() => setDialog({ branch: r, decision: 'rejected' })}
                      data-testid={`pc-branch-reject-${r.branch_id}`}>Reject</button>
                  </td>
                </tr>
              ))}
              {data.requests.length === 0 && (
                <tr><td colSpan={7} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }} data-testid="pc-no-branch-requests">
                  Nothing in this state.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <ConfirmAction
          open title={`${dialog.decision === 'approved' ? 'Approve' : dialog.decision === 'rejected' ? 'Reject' : 'Ask for more information about'} ${dialog.branch.name}?`}
          description={dialog.decision === 'approved'
            ? 'The branch becomes selectable at the till and appears on the customer portal.'
            : 'The pharmacy sees this reason and can resubmit.'}
          confirmWord={dialog.decision === 'rejected' ? 'REJECT' : ''}
          requireReason={dialog.decision !== 'approved'}
          busy={busy} onCancel={() => setDialog(null)} onConfirm={decide}
          testId="pc-branch-decision"
        />
      )}
    </div>
  );
};

export const PlatformRequestDesk = () => {
  const [status, setStatus] = useState('open');
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get('/platform/support-requests', { params: { status } })
      .then(r => setData(r.data)).catch(() => toast.error('Could not load the request desk'));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const update = async (note) => {
    setBusy(true);
    try {
      await api.post(`/platform/support-requests/${dialog.row.request_id}/status`,
        { status: dialog.next, note });
      toast.success(`Request ${dialog.next.replace('_', ' ')}`);
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="pc-request-desk">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 className="pc-h1">Pharmacy requests</h1>
          <p className="pc-sub">Raised from the read-only standing strip inside a pharmacy workspace.</p>
        </div>
        <select className="pc-select" style={{ width: 170 }} value={status}
          onChange={(e) => setStatus(e.target.value)} data-testid="pc-request-status-filter">
          {['open', 'in_progress', 'resolved', 'all'].map(s => (
            <option key={s} value={s}>{s.replace('_', ' ')}{data?.counts?.[s] != null ? ` · ${data.counts[s]}` : ''}</option>
          ))}
        </select>
      </div>

      {!data ? <div className="pc-muted" style={{ marginTop: '1.5rem' }}><Loader2 className="h-4 w-4 animate-spin" /></div> : (
        <div className="pc-card pc-scroll" style={{ marginTop: '1.1rem' }}>
          <table className="pc-table">
            <thead><tr><th>Pharmacy</th><th>Topic</th><th>Message</th><th>Raised</th><th>State</th><th /></tr></thead>
            <tbody>
              {data.requests.map(r => (
                <tr key={r.request_id} data-testid={`pc-request-${r.request_id}`}>
                  <td>
                    <Link to={`/platform/pharmacies/${r.tenant_id}`} className="pc-link">{r.pharmacy_name || r.tenant_id}</Link>
                    <div className="pc-muted">{r.raised_by_name}</div>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{(r.topic || '').replace(/_/g, ' ')}</td>
                  <td style={{ maxWidth: 380 }}>{r.message}
                    {(r.notes || []).map((n, i) => <div className="pc-muted" key={i}>“{n.note}” — {n.by}</div>)}
                  </td>
                  <td>{when(r.created_at)}</td>
                  <td><Pill value={r.status === 'resolved' ? 'approved' : r.status === 'open' ? 'pending' : r.status} /></td>
                  <td className="pc-num" style={{ whiteSpace: 'nowrap' }}>
                    {r.status !== 'in_progress' && r.status !== 'resolved' && (
                      <button className="pc-btn" onClick={() => setDialog({ row: r, next: 'in_progress' })}
                        data-testid={`pc-request-progress-${r.request_id}`}>Working on it</button>
                    )}{' '}
                    {r.status !== 'resolved' && (
                      <button className="pc-btn pc-btn-primary" onClick={() => setDialog({ row: r, next: 'resolved' })}
                        data-testid={`pc-request-resolve-${r.request_id}`}>Resolve</button>
                    )}
                  </td>
                </tr>
              ))}
              {data.requests.length === 0 && (
                <tr><td colSpan={6} className="pc-muted" style={{ textAlign: 'center', padding: '2rem' }} data-testid="pc-no-requests">
                  Nothing waiting.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <ConfirmAction
          open title={`Mark this request ${dialog.next.replace('_', ' ')}?`}
          description="The note is visible to the pharmacy on their standing page."
          confirmWord="" requireReason busy={busy}
          onCancel={() => setDialog(null)} onConfirm={update} testId="pc-request-update"
        />
      )}
    </div>
  );
};
